import { atom } from 'jotai';

/**
 * Map of product IDs to an object containing:
 * - timestamp: when the product was last modified
 * - fields: Set of field names that were modified
 * Used to highlight recently modified cells in the table.
 */
export interface RecentlyModifiedProduct {
  timestamp: number;
  fields: Set<string>;
}

export const recentlyModifiedProductsState = atom<
  Map<string, RecentlyModifiedProduct>
>(new Map());

/**
 * Duration in milliseconds to keep a product marked as recently modified.
 * Default: 5 seconds
 */
export const MODIFICATION_HIGHLIGHT_DURATION = 5000;

/**
 * Derived atom that returns a Map of product IDs to Sets of modified fields
 * that are currently considered "recently modified" (within the highlight duration).
 */
export const activeRecentlyModifiedFieldsState = atom<Map<string, Set<string>>>(
  (get) => {
    const modifiedMap = get(recentlyModifiedProductsState);
    const now = Date.now();
    const activeFields = new Map<string, Set<string>>();

    modifiedMap.forEach((data, productId) => {
      if (now - data.timestamp < MODIFICATION_HIGHLIGHT_DURATION) {
        activeFields.set(productId, new Set(data.fields));
      }
    });

    return activeFields;
  }
);

/**
 * Helper function to check if a specific field of a product is recently modified.
 * @param activeFields - The Map from activeRecentlyModifiedFieldsState
 * @param productId - The product ID to check
 * @param field - The field name to check
 * @returns true if the field was recently modified
 */
export const isFieldRecentlyModified = (
  activeFields: Map<string, Set<string>>,
  productId: string,
  field: string
): boolean => {
  const fields = activeFields.get(productId);
  return fields ? fields.has(field) : false;
};

/**
 * Represents a product modification with its ID and modified fields
 */
export interface ProductModification {
  productId: string;
  fields: string[];
}

/**
 * Field names that support "recently modified" highlighting in the table.
 * Used as fallback when the backend does not send changedFields (e.g. for
 * itemsCreated / datasourceProcessed from CSV, scraping, or copy previous sales).
 */
export const ALL_HIGHLIGHTABLE_FIELDS: string[] = [
  'name',
  'sku',
  'gtin',
  'description',
  'listPrice',
  'discountedPrice',
  'chooseDiscount',
  'choosePrice',
  'buyingPrice',
  'marginPercentage',
  'stockQuantity',
  'couponExpirationDate',
  'vatRate',
  'potentialSalesVolume',
  'madeIn',
  'shippingPrice',
  'productCategory',
  'sourceUrl',
  'eeePrice',
];

/**
 * Updates the recently modified products state by:
 * 1. Cleaning up expired entries (older than MODIFICATION_HIGHLIGHT_DURATION)
 * 2. Adding/updating newly modified products with their changed fields
 * 3. Merging fields with existing active entries
 *
 * @param previousState - The current state Map
 * @param currentTimestamp - Current timestamp (use Date.now())
 * @param modifications - Array of product modifications to add/update
 * @returns New state Map with cleaned up and updated entries
 */
export const updateRecentlyModifiedProducts = (
  previousState: Map<string, RecentlyModifiedProduct>,
  currentTimestamp: number,
  modifications: ProductModification[]
): Map<string, RecentlyModifiedProduct> => {
  const next = new Map<string, RecentlyModifiedProduct>();

  // Clean up expired entries and keep active ones
  previousState.forEach((data, productId) => {
    if (currentTimestamp - data.timestamp < MODIFICATION_HIGHLIGHT_DURATION) {
      // Keep active entries
      next.set(productId, {
        timestamp: data.timestamp,
        fields: new Set(data.fields),
      });
    }
    // Expired entries are automatically removed by not being added to next
  });

  // Add newly modified products with their changed fields
  modifications.forEach((modification) => {
    const { productId, fields } = modification;
    const existing = next.get(productId);
    const fieldsSet = new Set(fields);

    if (
      existing &&
      currentTimestamp - existing.timestamp < MODIFICATION_HIGHLIGHT_DURATION
    ) {
      // Merge with existing fields
      existing.fields.forEach((field) => fieldsSet.add(field));
    }

    next.set(productId, {
      timestamp: currentTimestamp,
      fields: fieldsSet,
    });
  });

  return next;
};
